/**
 * Webpage Chat Form Component
 * Form for creating new webpage chat content (Intercom-style chat for webpages)
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import {
  createWebpageChat,
  triggerWebpageCrawl,
} from "../../services/content.service";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import { QUERY_KEYS } from "../../utils/constants";
import {
  XMarkIcon,
  GlobeAltIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

interface WebpageChatFormProps {
  onClose: () => void;
  onComplete: () => void;
}

export default function WebpageChatForm({
  onClose,
  onComplete,
}: WebpageChatFormProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState("");

  // Crawl state
  const [crawlStatus, setCrawlStatus] = useState<
    "idle" | "creating" | "crawling" | "success" | "error"
  >("idle");
  const [crawlError, setCrawlError] = useState<string | null>(null);

  // Validation
  const [urlError, setUrlError] = useState<string | null>(null);

  const validateUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      if (!["http:", "https:"].includes(parsed.protocol)) {
        setUrlError("URL must start with http:// or https://");
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError("Please enter a valid URL");
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setSourceUrl(url);
    if (url) {
      validateUrl(url);
      // Auto-populate title from URL if not set
      if (!title) {
        try {
          const parsed = new URL(url);
          setTitle(parsed.hostname);
        } catch {
          // Ignore parsing errors
        }
      }
      // Auto-add domain to allowed domains
      try {
        const parsed = new URL(url);
        if (!allowedDomains.includes(parsed.hostname)) {
          setAllowedDomains([...allowedDomains, parsed.hostname]);
        }
      } catch {
        // Ignore parsing errors
      }
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleAddDomain = () => {
    const trimmedDomain = domainInput.trim().toLowerCase();
    if (trimmedDomain && !allowedDomains.includes(trimmedDomain)) {
      // Basic domain validation
      if (
        /^(\*\.)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(
          trimmedDomain,
        )
      ) {
        setAllowedDomains([...allowedDomains, trimmedDomain]);
        setDomainInput("");
      } else {
        showToast(
          "error",
          "Invalid domain format. Use format: example.com or *.example.com",
        );
      }
    }
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domainToRemove));
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      setCrawlStatus("creating");

      // Create the content item
      const contentItem = await createWebpageChat(sourceUrl, title, user.id, {
        description,
        tags,
        is_public: isPublic,
        allowed_domains: allowedDomains,
      });

      // Trigger crawling
      setCrawlStatus("crawling");
      await triggerWebpageCrawl(contentItem.id);

      return contentItem;
    },
    onSuccess: () => {
      setCrawlStatus("success");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      showToast(
        "success",
        "Webpage chat created and crawling started. The page will be indexed shortly.",
      );
      setTimeout(() => {
        onComplete();
      }, 1500);
    },
    onError: (error: Error) => {
      setCrawlStatus("error");
      setCrawlError(error.message);
      showToast("error", `Failed to create webpage chat: ${error.message}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      showToast("error", "Please enter a title");
      return;
    }

    if (!sourceUrl.trim()) {
      showToast("error", "Please enter a source URL");
      return;
    }

    if (!validateUrl(sourceUrl)) {
      return;
    }

    await createMutation.mutateAsync();
  };

  const isLoading = crawlStatus === "creating" || crawlStatus === "crawling";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Add Webpage Chat
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Intro Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                Create an AI chat widget that can answer questions about a
                webpage. The page content will be crawled and indexed for
                intelligent Q&A.
              </p>
            </div>

            {/* Source URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source URL <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={handleUrlChange}
                  placeholder="https://www.example.com/page"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    urlError ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                  disabled={isLoading}
                />
              </div>
              {urlError && (
                <p className="mt-1 text-sm text-red-500">{urlError}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The webpage content will be crawled and indexed for the chat
              </p>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My Webpage Chat"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={isLoading}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={isLoading}
              />
            </div>

            {/* Allowed Domains */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allowed Domains
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Domains where this chat widget can be embedded. Use *.domain.com
                for subdomains.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {allowedDomains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full"
                  >
                    {domain}
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="text-green-600 hover:text-red-500"
                      disabled={isLoading}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddDomain();
                    }
                  }}
                  placeholder="example.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddDomain}
                  disabled={isLoading}
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-gray-500 hover:text-red-500"
                      disabled={isLoading}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddTag}
                  disabled={isLoading}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={isLoading}
              />
              <label htmlFor="isPublic" className="text-sm text-gray-700">
                Make publicly accessible (required for embedding)
              </label>
            </div>

            {/* Status Display */}
            {crawlStatus !== "idle" && (
              <div
                className={`rounded-lg p-4 ${
                  crawlStatus === "error"
                    ? "bg-red-50 border border-red-200"
                    : crawlStatus === "success"
                      ? "bg-green-50 border border-green-200"
                      : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {crawlStatus === "creating" && (
                    <>
                      <ArrowPathIcon className="h-5 w-5 text-blue-600 animate-spin" />
                      <span className="text-sm font-medium text-blue-700">
                        Creating content item...
                      </span>
                    </>
                  )}
                  {crawlStatus === "crawling" && (
                    <>
                      <ArrowPathIcon className="h-5 w-5 text-blue-600 animate-spin" />
                      <span className="text-sm font-medium text-blue-700">
                        Crawling webpage and indexing content...
                      </span>
                    </>
                  )}
                  {crawlStatus === "success" && (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        Created successfully! Indexing in progress.
                      </span>
                    </>
                  )}
                  {crawlStatus === "error" && (
                    <>
                      <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-medium text-red-700">
                        {crawlError || "An error occurred"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                disabled={crawlStatus === "success"}
              >
                {isLoading ? "Creating..." : "Create & Crawl"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
